// frontend/src/components/EventsListModal.js
import React, { useEffect, useMemo, useState } from "react";
import { api } from "api";
import { X, MapPin } from "phosphor-react";
import LocationAutoComplete from "./LocationAutoComplete";
import DateTimeField12h from "./DateTimeField12h";

/** Local "YYYY-MM-DDTHH:MM" from any ISO-ish input */
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${mi}`;
}

const EventsListModal = ({ open, onClose, events = [], isAdmin, onChanged, highlightEventId = null }) => {
  const [manage, setManage] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [tab, setTab] = useState("future");

  const { futureEvents, pastEvents } = useMemo(() => {
    const now = Date.now();
    const deduped = [];
    const seen = new Set();

    events.forEach((e) => {
      if (!e?.id || !e?.start_time) return;
      if (seen.has(e.id)) return;
      seen.add(e.id);
      deduped.push(e);
    });

    return {
      futureEvents: deduped
        .filter((e) => new Date(e.start_time).getTime() >= now)
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
      pastEvents: deduped
        .filter((e) => new Date(e.start_time).getTime() < now)
        .sort((a, b) => new Date(b.start_time) - new Date(a.start_time)),
    };
  }, [events]);

  const visibleEvents = tab === "future" ? futureEvents : pastEvents;

  useEffect(() => {
    if (!open) return;

    const init = {};
    events.forEach((e) => {
      init[e.id] = {
        title: e.title || "",
        start_time: toLocalInput(e.start_time) || "",
        location_text: e.location_text || "",
        location_url: e.location_url || "",
        description: e.description || "",
      };
    });

    setDrafts(init);
    setManage(false);
    setEditingId(null);
    setSavingId(null);
    setTab(futureEvents.length > 0 ? "future" : "past");
  }, [open, events, futureEvents.length]);

  if (!open) return null;

  const onEdit = (id) => setEditingId(id);
  const onCancel = () => setEditingId(null);

  const updateDraft = (id, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        ...patch,
      },
    }));
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;

    try {
      setSavingId(id);
      await api.delete(`/groups/events/${id}`);
      setSavingId(null);
      onChanged?.("deleted", id);
    } catch {
      setSavingId(null);
      onChanged?.("error");
    }
  };

  const onSave = async (id) => {
    const d = drafts[id] || {};

    const payload = {
      title: (d.title || "").trim(),
      start_time: d.start_time ? new Date(d.start_time).toISOString() : null,
      description: d.description?.trim() || null,
      location_text: d.location_text?.trim() || null,
      location_url: d.location_url?.trim() || null,
    };

    try {
      setSavingId(id);
      await api.patch(`/groups/events/${id}`, payload);
      setSavingId(null);
      setEditingId(null);
      onChanged?.("updated", id);
    } catch {
      setSavingId(null);
      onChanged?.("error");
    }
  };

  return (
    <div className="fixed inset-0 z-[75] grid place-items-center bg-black/35 overflow-y-auto p-0 md:p-6">
      <div
        className="
          relative z-10 w-full max-w-xl overflow-hidden
          rounded-[28px] border border-blue-100/80
          bg-white text-blue-950
          p-4 md:p-6
          shadow-[0_8px_0px_rgba(37,99,235,0.96)]
        "
        role="dialog"
        aria-modal="true"
        aria-labelledby="events-title"
      >
        <button
          onClick={onClose}
          className="
            absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center
            rounded-full text-gray-700
            transition hover:bg-blue-200
          "
          aria-label="Close"
        >
          <X size={19} weight="bold" />
        </button>

        <div className="text-center">
          <h3
            id="events-title"
            className="text-blue-600 font-black leading-none tracking-[-0.04em] text-[22px] sm:text-[22px]"
          >
            Events
          </h3>
        </div>

        <div className="mt-5">
          <div className="mb-3 flex items-end justify-between gap-3">
            <div className="inline-flex rounded-full bg-blue-50 p-1">
              <button
                type="button"
                onClick={() => setTab("future")}
                className={[
                  "h-8 rounded-full px-3 text-sm font-semibold transition",
                  tab === "future"
                    ? "bg-blue-600 text-white"
                    : "text-blue-600 hover:bg-white",
                ].join(" ")}
              >
                Upcoming Events
                {futureEvents.length > 0 ? ` (${futureEvents.length})` : ""}
              </button>

              <button
                type="button"
                onClick={() => setTab("past")}
                className={[
                  "h-8 rounded-full px-3 text-sm font-semibold transition",
                  tab === "past"
                    ? "bg-blue-600 text-white"
                    : "text-blue-600 hover:bg-white",
                ].join(" ")}
              >
                Past Events
                {pastEvents.length > 0 ? ` (${pastEvents.length})` : ""}
              </button>
            </div>

            {isAdmin && (
              <button
                type="button"
                onClick={() => setManage((m) => !m)}
                className="
                  inline-flex h-8 items-center justify-center rounded-full
                  bg-blue-700 px-3 text-sm font-medium text-white
                  transition 
                "
              >
                {manage ? "Done" : "Manage"}
              </button>
            )}
          </div>

          <div className="max-h-[70dvh] overflow-y-auto">
            {visibleEvents.length === 0 ? (
              <div className="rounded-2xl border border-blue-100 bg-white/90 px-3 py-4 text-sm font-semibold text-blue-500">
                {tab === "future" ? "No upcoming events" : "No past events"}
              </div>
            ) : (
              <div className="space-y-2.5">
                {visibleEvents.map((ev) => {
                  const isEditing = editingId === ev.id;
                  const row = drafts[ev.id] || {};
                  const dateObj = new Date(ev.start_time);
                  const canSave = row.title?.trim() && row.start_time;

                  return (
                    <div
                      key={ev.id}
                      className={[
                        "rounded-3xl bg-blue-50 px-4 py-4",
                        String(ev.id) === String(highlightEventId)
                          ? "border-2 border-blue-600"
                          : "",
                      ].join(" ")}
                    >
                      <div className="flex items-start gap-4">
                        {!isEditing && (
                          <div className="shrink-0 rounded-2xl bg-white px-3 py-3 shadow-[0px_4px_0px_rgba(37,99,235,0.96)] text-center w-14">
                            <div className="text-lg font-black -mb-1.5 text-amber-500">
                              {String(dateObj.getDate())}
                            </div>
                            <div className="text-sm font-bold text-gray-500">
                              {dateObj
                                .toLocaleString("en-US", { month: "short" })
                                .toUpperCase()}
                            </div>
                          </div>
                        )}

                        <div className="min-w-0 flex-1">
                          {!isEditing ? (
                            <>
                              <div className="text-sm font-medium leading-tight text-gray-600">
                                {dateObj.toLocaleString("en-US", {
                                  weekday: "short",
                                  hour: "numeric",
                                  minute: "2-digit",
                                })}
                              </div>

                              <div className="sm:text-[16px] text-sm font-bold leading-snug break-words text-blue-900">
                                {ev.title}
                              </div>

                              {(ev.location_text || ev.location_url) && (
                                <div className="flex items-center gap-0.5 text-[13px] text-blue-600 break-words">
                                  <MapPin
                                    size={13}
                                    weight="fill"
                                    className="shrink-0 mb-0.5 text-blue-600"
                                  />

                                  {ev.location_url ? (
                                    <a
                                      href={ev.location_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-600 hover:underline break-words"
                                    >
                                      {ev.location_text ||
                                        ev.location_url.replace(/^https?:\/\//, "")}
                                    </a>
                                  ) : (
                                    <span>{ev.location_text}</span>
                                  )}
                                </div>
                              )}

                              {ev.description && (
                                <div className="mt-1.5 text-sm leading-5 text-blue-900/75 whitespace-pre-wrap break-words">
                                  {ev.description}
                                </div>
                              )}
                            </>
                          ) : (
                            <form
                              onSubmit={(e) => {
                                e.preventDefault();
                                if (canSave && savingId !== ev.id) onSave(ev.id);
                              }}
                              className="space-y-1"
                            >
                              <div>
                                <label className="block text-sm font-medium mb-1 ml-3">
                                  Event Name
                                </label>
                                <input
                                  type="text"
                                  value={row.title || ""}
                                  onChange={(e) =>
                                    updateDraft(ev.id, { title: e.target.value })
                                  }
                                  className="w-full border rounded-2xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                                  placeholder="Cooking Class @Sur La Table"
                                  required
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium mb-1 ml-3">
                                  Date & Time
                                </label>
                                <DateTimeField12h
                                  value={row.start_time || ""}
                                  onChange={(val) =>
                                    updateDraft(ev.id, { start_time: val })
                                  }
                                  minuteStep={5}
                                  className="w-full text-sm"
                                  wideDate
                                />
                              </div>

                              <div>
                                <label className="block text-sm ml-3 font-medium mb-1">
                                  Location
                                </label>
                                <div className="relative z-[1000] overflow-visible">
                                  <LocationAutoComplete
                                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                                    value={row.location_text || ""}
                                    onChange={(val) =>
                                      updateDraft(ev.id, { location_text: val })
                                    }
                                    inputClassName="py-1"
                                    placeholder=""
                                    country="us"
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm ml-3 font-medium mb-1">
                                  Description
                                </label>
                                <textarea
                                  rows={4}
                                  value={row.description || ""}
                                  onChange={(e) =>
                                    updateDraft(ev.id, {
                                      description: e.target.value,
                                    })
                                  }
                                  className="w-full border rounded-2xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium ml-3 mb-1">
                                  Online Link
                                </label>
                                <input
                                  type="url"
                                  value={row.location_url || ""}
                                  onChange={(e) =>
                                    updateDraft(ev.id, {
                                      location_url: e.target.value,
                                    })
                                  }
                                  placeholder="Zoom/Meet link or any URL"
                                  className="w-full border rounded-2xl p-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                                />
                              </div>

                              <div className="flex justify-end gap-2 pt-2">
                                <button
                                  type="button"
                                  onClick={onCancel}
                                  disabled={savingId === ev.id}
                                  className="bg-white text-blue-800 text-sm rounded-full px-3 !min-h-0 !h-7 disabled:opacity-50"
                                >
                                  Cancel
                                </button>

                                <button
                                  type="submit"
                                  disabled={!canSave || savingId === ev.id}
                                  className="bg-blue-600 text-white text-sm rounded-full px-3 !min-h-0 !h-7 disabled:opacity-50"
                                >
                                  {savingId === ev.id ? "Saving…" : "Save"}
                                </button>
                              </div>
                            </form>
                          )}
                        </div>

                        {isAdmin && manage && !isEditing && (
                          <div className="shrink-0 flex flex-col items-end">
                            <button
                              type="button"
                              className="
                                inline-flex items-center gap-1 rounded-full
                                bg-transparent px-2.5 !hmin-h-0 !h-5
                                text-xs font-medium text-blue-800 hover:font-bold
                              "
                              onClick={() => onEdit(ev.id)}
                              title="Edit event"
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className="
                                inline-flex items-center rounded-full
                                bg-transparent px-2.5 !hmin-h-0 !h-5
                                text-xs font-medium text-red-700
                                transition disabled:opacity-75 hover:font-bold
                              "
                              onClick={() => onDelete(ev.id)}
                              disabled={savingId === ev.id}
                              title="Delete event"
                            >
                              {savingId === ev.id ? "Deleting…" : "Delete"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsListModal;